'use client';

import { cn } from '@/lib/utils';
import { HelpCentreButton } from '@/components/shared/help-centre-button';
import { ThemeToggle } from '@/components/shared/theme-toggle';

export interface HeaderActionsProps {
  /**
   * Page-level actions, rendered to the LEFT of the global chrome. The global
   * controls stay pinned to the corner so their position never depends on what
   * a given page happens to put beside them.
   */
  children?: React.ReactNode;
  /**
   * Whether this cluster is the page's global-chrome corner. `false` for a
   * header nested inside another that already owns the corner — a matter tab
   * inside `TabbedDetailShell`, say — where repeating Help and the theme
   * picker would duplicate them on one screen.
   */
  showGlobalActions?: boolean;
  className?: string;
}

/**
 * The top-right header cluster: page actions, then the app's global controls
 * (VALE-520 · DARK-3).
 *
 * ONE component, five headers. Before this, `HelpCentreButton` was mounted five
 * times independently (`page-shell`, `tabbed-detail-shell`,
 * `details-sidebar/tab-header`, `contacts-header`, `safe-custody-header`) with
 * three different gaps and, in two of them, no wrapper at all. Adding the theme
 * toggle would have meant a sixth, seventh… copy of the same decision, so the
 * cluster is now the thing that is mounted and the buttons inside it are an
 * implementation detail.
 *
 * **Adding a global header control is now a one-line change here** — which is
 * the point. Anything that belongs in every page's corner goes in this file;
 * anything page-specific comes in as `children`.
 */
export function HeaderActions({
  children,
  showGlobalActions = true,
  className
}: HeaderActionsProps) {
  return (
    <div className={cn('flex shrink-0 items-center gap-2', className)}>
      {children}
      {showGlobalActions && (
        <>
          <ThemeToggle />
          <HelpCentreButton />
        </>
      )}
    </div>
  );
}
